import { getDecimals } from '../actions/public/getDecimals.js';
import { SDKRangeError, SDKReferenceError } from '../errors/primitives.js';
import { ContextHost } from '../service/contextHost.js';
import { httpServiceHost, jsonRpcServiceHost } from '../serviceHost.js';
import { calcGasFee } from '../utils/gas.js';
import { rpc } from '../utils/request/rpc.js';
import { BigNumber } from 'bignumber.js';
import { formatUnits, parseUnits, zeroAddress, } from 'viem';
const MAX_FRAC_DIGITS = 20;
const MIN_FRAC_DIGITS = 0;
export class Price extends ContextHost {
    getPairRate = async (dto) => {
        const { result } = await rpc(this.host(jsonRpcServiceHost), {
            body: {
                method: 'api.getMarketPrice',
                params: dto,
            },
        });
        return result;
    };
    getTokensPrice = async (tokens, currency = 'USD') => {
        const dto = tokens.map((addr) => ({
            caip2: `eip155:${this.chainId}`,
            address: addr,
        }));
        const { result } = await rpc(`${this.host(httpServiceHost)}/v1/market`, {
            body: {
                method: 'market.getPrices',
                params: dto,
            },
            fetchOptions: {
                headers: {
                    'x-currency': currency,
                },
            },
        });
        return result;
    };
    getTokenPrice = async (token, currency = 'USD') => {
        const result = await this.getTokensPrice([token], currency);
        return result[0];
    };
    getGasCost = async ({ gas, gasFeeValue, decimals = 18, currency = 'USD', maximumFractionDigits = 6, speedup = true, }) => {
        if (maximumFractionDigits > MAX_FRAC_DIGITS ||
            maximumFractionDigits < MIN_FRAC_DIGITS) {
            throw new SDKRangeError('maximumFractionDigits', {
                min: MIN_FRAC_DIGITS,
                max: MAX_FRAC_DIGITS,
            });
        }
        const { price: etherPrice } = (await this.getTokenPrice(zeroAddress)) ?? {
            price: 0,
        };
        if (!etherPrice) {
            throw new SDKReferenceError('price', `eth price not found for currency: ${currency}`);
        }
        const gasWei = calcGasFee({ gas, gasFeeValue }, speedup) ?? 0n;
        const parsedEthPrice = parseUnits(etherPrice.toString(), MAX_FRAC_DIGITS);
        const gasCost = (gasWei * parsedEthPrice) / BigInt(10 ** decimals);
        const maxFracGasCost = Number(gasCost) / 10 ** MAX_FRAC_DIGITS;
        return Number(maxFracGasCost.toFixed(maximumFractionDigits));
    };
    getTokenValue = async (address, amount, currency = 'USD') => {
        const decimals = await getDecimals(this.publicClient, address);
        const tokenPrice = await this.getTokenPrice(address, currency);
        if (!tokenPrice || !tokenPrice.price) {
            throw new SDKReferenceError('price', `price not found for currency: ${currency}`);
        }
        const parsedAmount = formatUnits(BigInt(amount), decimals);
        return Number(parsedAmount) * tokenPrice.price;
    };
    convertTokenValueToAmount = async ({ value, tokenAddress, }) => {
        const tokenPrice = await this.getTokenPrice(tokenAddress);
        if (!tokenPrice || !tokenPrice.price) {
            throw new SDKReferenceError('price', `price not found for token: ${tokenAddress}`);
        }
        const decimals = await getDecimals(this.publicClient, tokenAddress);
        const formattedAmount = new BigNumber(value.toString()).dividedBy(tokenPrice.price);
        const parsedAmount = parseUnits(formattedAmount.toFixed(decimals, BigNumber.ROUND_FLOOR), decimals);
        return parsedAmount;
    };
}
//# sourceMappingURL=index.js.map